import React, { useEffect, useMemo, useState } from 'react';
import { Modal, ModalBody, ModalHeader, Button } from 'reactstrap';
import { FooterButtons } from '../../../shared/components/Modals/FooterButtons';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import classNames from 'classnames';
import ImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';
import { fetchAndPrepareFile } from './utils';
import { getCloudinaryDeliveryUrl } from '../../../utils/cloudinaryDelivery';

const IMAGE_GALLERY_MAIN_TRANSFORM = 'c_limit,w_456,h_630,q_auto,f_auto';
const IMAGE_GALLERY_THUMB_TRANSFORM = 'c_limit,w_160,h_192,q_auto,f_auto';
const renderLeftNav = (onClick, disabled) => (
  <button
    type="button"
    className="image-gallery-icon image-gallery-left-nav"
    disabled={disabled}
    onClick={onClick}
    aria-label="Previous Slide"
  >
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M15.41 16.58L10.83 12L15.41 7.41L14 6L8 12L14 18L15.41 16.58Z"
        fill="#00B0EC"
      />
    </svg>
  </button>
);

const renderRightNav = (onClick, disabled) => (
  <button
    type="button"
    className="image-gallery-icon image-gallery-right-nav"
    disabled={disabled}
    onClick={onClick}
    aria-label="Next Slide"
  >
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8.58984 16.58L13.1698 12L8.58984 7.41L9.99984 6L15.9998 12L9.99984 18L8.58984 16.58Z"
        fill="#00B0EC"
      />
    </svg>
  </button>
);

export function ImageGalleryModal({
  isOpen,
  onClose,
  title = 'Image Gallery',
  images = [],
  modalSize = 'lg',
  modalClassName = '',
  onSave = undefined,
  zipName = 'images.zip',
  initialIndex = 0,
}) {
  const safeInitialIndex = useMemo(() => {
    if (!images.length) {
      return 0;
    }
    const parsed = Number(initialIndex);
    const index = Number.isFinite(parsed) ? Math.trunc(parsed) : 0;
    return Math.min(Math.max(0, index), images.length - 1);
  }, [images, initialIndex]);

  const [currentIndex, setCurrentIndex] = useState(safeInitialIndex);

  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(safeInitialIndex);
    }
  }, [isOpen, safeInitialIndex]);

  const galleryItems = useMemo(() => {
    return images.map((imageUrl, index) => ({
      original: getCloudinaryDeliveryUrl(
        imageUrl,
        IMAGE_GALLERY_MAIN_TRANSFORM,
      ),
      thumbnail: getCloudinaryDeliveryUrl(
        imageUrl,
        IMAGE_GALLERY_THUMB_TRANSFORM,
      ),
      originalAlt: `Image ${index + 1}`,
      thumbnailAlt: `Thumbnail ${index + 1}`,
    }));
  }, [images]);

  const handleSlide = (nextIndex) => {
    setCurrentIndex(nextIndex);
  };

  const downloadCurrentImage = async () => {
    if (images.length === 0) return;

    try {
      const currentImageUrl = images[currentIndex];
      const { fileName, blob } = await fetchAndPrepareFile(
        currentImageUrl,
        currentIndex,
      );
      saveAs(blob, fileName);
    } catch (err) {
      console.error('Error downloading image:', err);
    }
  };

  const downloadAllImages = async () => {
    if (images.length === 0) return;

    const zip = new JSZip();

    const promises = images.map(async (url, i) => {
      const { fileName, blob } = await fetchAndPrepareFile(url, i);
      zip.file(fileName, blob);
    });

    try {
      await Promise.all(promises);
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, zipName);
    } catch (error) {
      console.error('Error creating archive:', error);
    }
  };

  const handleImageClick = () => {
    if (images.length === 0) return;

    const currentImageUrl = images[currentIndex];
    window.open(currentImageUrl, '_blank');
  };

  return (
    <Modal
      isOpen={isOpen}
      size={modalSize}
      keyboard
      toggle={onClose}
      className={classNames(
        'nested-modal image-gallery-modal model-form',
        modalClassName,
      )}
    >
      <ModalHeader>
        <h3>{title}</h3>
      </ModalHeader>
      <ModalBody>
        <div className={classNames('images-container')}>
          {images.length > 0 ? (
            <div className="gallery-wrapper">
              {isOpen ? (
                <ImageGallery
                  key={`image-gallery-${safeInitialIndex}-${images.length}`}
                  items={galleryItems}
                  startIndex={safeInitialIndex}
                  showPlayButton={false}
                  showFullscreenButton={false}
                  showNav={images.length > 1}
                  thumbnailPosition="bottom"
                  slideDuration={250}
                  slideInterval={1000}
                  onSlide={handleSlide}
                  renderLeftNav={renderLeftNav}
                  renderRightNav={renderRightNav}
                  onClick={handleImageClick}
                  additionalClass="custom-image-gallery"
                  renderItem={(item) => (
                    <div className="image-gallery-image">
                      <img
                        src={item.original}
                        alt={item.originalAlt}
                        loading="lazy"
                        title="Click to open in a new window"
                      />
                    </div>
                  )}
                />
              ) : null}
            </div>
          ) : (
            <div className="no-images-text">
              <p>No images to display</p>
            </div>
          )}
        </div>

        <FooterButtons
          onClose={onClose}
          showSaveButton={!!onSave}
          onSave={onSave}
          additionalButtons={
            <>
              <Button
                color="primary"
                onClick={downloadCurrentImage}
                disabled={images.length === 0}
              >
                Download
              </Button>
              <Button
                color="primary"
                disabled={images.length === 0}
                onClick={downloadAllImages}
              >
                Download all
              </Button>
            </>
          }
        />
      </ModalBody>
    </Modal>
  );
}
